import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Org } from '../../../enum';
import { APIStatus } from '../../../enum';
import { removeEmpty } from '../../../helpers/utils.helper';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig, DefinedFields } from '../../../models/slice';
import { type FilteredInfo, type RequestParams, SortedInfo, type TableChange } from '../../../models/table';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import { validateAdminSPOrganization } from './admin-service-provider-organizations.helper';

const { logError } = RaygunErrorHandlerService();

export class AdminSPOrganization {
  total_count?: number = undefined;
  id: string | undefined = undefined;
  name: string | undefined = undefined;
  internal: boolean | undefined = undefined;
  org_code: Org | undefined = undefined;
  org_name: string | undefined = undefined;
  description: string | null | undefined = undefined;
  website: string | null | undefined = undefined;
  phone_number: string | null | undefined = undefined;
}

export type SPOrganization = DefinedFields<InstanceType<typeof AdminSPOrganization>>;

export const ADMIN_SP_ORGANIZATION_MODAL = {
  IDLE: 'idle',
  EDIT: 'edit',
} as const;

export type ADMIN_SP_ORGANIZATION_MODAL =
  (typeof ADMIN_SP_ORGANIZATION_MODAL)[keyof typeof ADMIN_SP_ORGANIZATION_MODAL];

type AdminSPOrganizationsSliceType = {
  adminSPOrgsForTable: SPOrganization[];
  adminSPOrgs: SPOrganization[];
  requestAdminSPOrganizationsParams: RequestParams<AdminSPOrganization>;
  adminSPOrganizationsApiStatus: APIStatus;
  adminSPOrganizationsModal: ADMIN_SP_ORGANIZATION_MODAL;
  adminSPOrganization: AdminSPOrganization;
};

const initialState: AdminSPOrganizationsSliceType = {
  adminSPOrgsForTable: [],
  adminSPOrgs: [],
  requestAdminSPOrganizationsParams: {
    pagination: {
      current: 1,
      pageSize: 50,
      defaultPageSize: 50,
    },
    sortedInfo: new SortedInfo<AdminSPOrganization>('name', 'ascend'),
    filteredInfo: {},
  },
  adminSPOrganizationsApiStatus: APIStatus.IDLE,
  adminSPOrganizationsModal: ADMIN_SP_ORGANIZATION_MODAL.IDLE,
  adminSPOrganization: new AdminSPOrganization(),
};

export const fetchAdminSPOrgsForTable = createAsyncThunk<SPOrganization[], undefined, AsyncThunkConfig>(
  'adminSPOrganizations/fetchAdminSPOrgsForTable',
  async (_, thunkAPI) => {
    try {
      const params = thunkAPI.getState().adminSPOrganizationsSlice.requestAdminSPOrganizationsParams;
      const response = (await axios.post('v0_get_service_provider_organizations', params)) as SPOrganization[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminSPOrganizationsSlice', 'fetchAdminSPOrgsForTable']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchSPOrgs = createAsyncThunk<SPOrganization[], undefined, AsyncThunkConfig>(
  'adminSPOrganizations/fetchSPOrgs',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_get_service_provider_manager_organizations', {
        sortedInfo: new SortedInfo<SPOrganization>('name', 'ascend'),
      })) as SPOrganization[];
      return response ?? [];
    } catch (e) {
      logError(e, ['adminSPOrganizationsSlice', 'fetchSPOrgs']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

type AdminSPOrganizationUpsertResponse = {
  id: string;
  is_update: boolean;
};

export const upsertAdminSPOrganization = createAsyncThunk<
  AdminSPOrganizationUpsertResponse | undefined,
  AdminSPOrganization,
  AsyncThunkConfig
>('adminSPOrganizations/upsertAdminSPOrganization', async (upsertAdminSPOrganization, thunkAPI) => {
  try {
    if (validateAdminSPOrganization(upsertAdminSPOrganization)) {
      const response = (await axios.post(
        'v0_upsert_service_provider_organization',
        upsertAdminSPOrganization,
      )) as AdminSPOrganizationUpsertResponse;
      if (response) {
        thunkAPI.dispatch(fetchAdminSPOrgsForTable());
        thunkAPI.dispatch(closeAdminSPOrganizationModal());
        notification.success({
          title: `Service provider organization ${response.is_update ? 'updated' : 'added'} successfully`,
          description: 'Success',
        });
      }
      return response;
    }
  } catch (e) {
    logError(e, ['adminSPOrganizationsSlice', 'upsertAdminSPOrganization']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const adminSPOrganizationsSlice = createSlice({
  name: 'adminSPOrganizations',
  initialState,
  reducers: {
    handleAdminSPOrganizationsTableChange: (state, action: PayloadAction<TableChange<AdminSPOrganization>>) => {
      const filters = action.payload.filters as FilteredInfo;
      const sorter = action.payload.sorter as SortedInfo<AdminSPOrganization>;
      state.requestAdminSPOrganizationsParams.pagination = action.payload.pagination;
      state.requestAdminSPOrganizationsParams.sortedInfo = sorter;
      state.requestAdminSPOrganizationsParams.filteredInfo = removeEmpty(filters);
    },
    clearAdminSPOrganizationsFilter: (state) => {
      state.requestAdminSPOrganizationsParams.pagination = initialState.requestAdminSPOrganizationsParams.pagination;
      state.requestAdminSPOrganizationsParams.sortedInfo = initialState.requestAdminSPOrganizationsParams.sortedInfo;
      state.requestAdminSPOrganizationsParams.filteredInfo =
        initialState.requestAdminSPOrganizationsParams.filteredInfo;
    },
    showEditModal: (state, action: PayloadAction<ADMIN_SP_ORGANIZATION_MODAL>) => {
      state.adminSPOrganizationsModal = action.payload;
    },
    setAdminSPOrganization: (state, action: PayloadAction<AdminSPOrganization>) => {
      state.adminSPOrganization = action.payload;
    },
    closeAdminSPOrganizationModal: (state) => {
      state.adminSPOrganizationsModal = ADMIN_SP_ORGANIZATION_MODAL.IDLE;
      state.adminSPOrganization = new AdminSPOrganization();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminSPOrgsForTable.pending, (state, _action) => {
        state.adminSPOrganizationsApiStatus = APIStatus.PENDING;
      })
      .addCase(fetchAdminSPOrgsForTable.fulfilled, (state, action) => {
        state.adminSPOrgsForTable = action.payload;
        state.adminSPOrganizationsApiStatus = APIStatus.FULFILLED;
      })
      .addCase(fetchAdminSPOrgsForTable.rejected, (state, _action) => {
        state.adminSPOrganizationsApiStatus = APIStatus.ERROR;
      })
      .addCase(fetchSPOrgs.fulfilled, (state, action) => {
        state.adminSPOrgs = action.payload;
      });
  },
});

export const {
  handleAdminSPOrganizationsTableChange,
  clearAdminSPOrganizationsFilter,
  showEditModal,
  setAdminSPOrganization,
  closeAdminSPOrganizationModal,
} = adminSPOrganizationsSlice.actions;
